import React from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu-12';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
  IssueTransitionFormRowData,
  IssueTransition,
} from 'src/redux/jira/types';

import messages from './issue-transition-form-row.i18n';
import * as styles from './issue-transition-form-row.style';
import StatusLozenge from './status-lozenge';

type IssueTransitionFormRowProps = {
  intl: InjectedIntl;
  issueTransitionFormRow: IssueTransitionFormRowData;
  updateIssueTransition: (row: IssueTransitionFormRowData) => void;
};

/* eslint @typescript-eslint/ban-types: "warn" */
const IssueTransitionFormRow: React.FC<IssueTransitionFormRowProps> = ({
  intl,
  issueTransitionFormRow,
  updateIssueTransition,
}) => {
  const handleCheckboxClick = () => {
    updateIssueTransition({
      ...issueTransitionFormRow,
      shouldTransition: !issueTransitionFormRow.shouldTransition,
    });
  };
  const onChangeTransitionSelect = (selectedTransition: IssueTransition) => {
    updateIssueTransition({
      ...issueTransitionFormRow,
      shouldTransition: true,
      selectedTransition,
    });
  };

  const {
    selectedTransition,
    selectedIssue,
    availableIssueTransitions,
    hasUnsupportedTransitions,
  } = issueTransitionFormRow;

  return (
    <styles.LineContainer>
      <styles.TransitionContainer>
        <Checkbox
          isChecked={issueTransitionFormRow.shouldTransition}
          onChange={handleCheckboxClick}
        />
        <Tooltip
          content={`${selectedIssue.issue.key}: ${selectedIssue.issue.summary}`}
        >
          <styles.IssueContainer>
            <img
              src={selectedIssue.issue.issueType.iconUrl}
              alt={selectedIssue.issue.issueType.name}
            />
            <styles.IssueKeyContainer>
              {selectedIssue.issue.key}
            </styles.IssueKeyContainer>
            <styles.IssueSummaryContainer>
              {selectedIssue.issue.summary}
            </styles.IssueSummaryContainer>
          </styles.IssueContainer>
        </Tooltip>
        <styles.ArrowContainer>
          <ArrowRightIcon label="→" size="small" />
        </styles.ArrowContainer>
        <styles.SelectContainer>
          <DropdownMenu
            trigger={({ triggerRef, ...props }) => (
              <Button
                {...props}
                iconAfter={<ChevronDownIcon label="" />}
                ref={triggerRef}
                appearance="subtle"
                spacing="none"
              >
                {selectedTransition ? (
                  <styles.TriggerContainer>
                    <StatusLozenge transition={selectedTransition} />
                  </styles.TriggerContainer>
                ) : (
                  <styles.TriggerContainer>
                    {intl.formatMessage(messages.status)}
                  </styles.TriggerContainer>
                )}
              </Button>
            )}
            spacing="compact"
            testId="issues-transition-dropdown"
          >
            {selectedTransition && (
              <DropdownItemGroup>
                {availableIssueTransitions.map(transition => (
                  <DropdownItem
                    key={transition.id}
                    onClick={() => onChangeTransitionSelect(transition)}
                  >
                    <Tooltip content={transition.name}>
                      <StatusLozenge transition={transition} />
                    </Tooltip>
                  </DropdownItem>
                ))}
              </DropdownItemGroup>
            )}
          </DropdownMenu>
        </styles.SelectContainer>
        {hasUnsupportedTransitions && (
          <Tooltip content={intl.formatMessage(messages.notSupported)}>
            <styles.WarningContainer>
              <InfoIcon
                label={intl.formatMessage(messages.notSupported)}
                primaryColor={token('color.icon', colors.N300)}
                size="small"
              />
            </styles.WarningContainer>
          </Tooltip>
        )}
      </styles.TransitionContainer>
    </styles.LineContainer>
  );
};

export default injectIntl(IssueTransitionFormRow);
